import { Card } from "../../Components/Card";
import { Circle } from "../../Components/Circle";
import { createContext, useContext, useEffect, useRef } from "react";
import { useFetch } from "../../hooks/useFetch";
import hiClient from "../../axios/hiClient";
import * as echarts from "echarts";
import { generateCircularProgressBarOption } from "../../Components/PieChart";
import Recommendations from "../../Components/Recommendations";
import { useTranslation } from "react-i18next";
import { Colors } from "../../constants/Colors";


const DataContext = createContext();

export const HiDeviceDetailAnalysis = ({ id }) => {
  const { data, isLoading, error } = useFetch({
    url: `/mobile/detail?id=${id}`,
    client: hiClient,
    initialData: {
      success: false,
      id: "",
      tgl_ukur: "",
      name: "",
      status: "",
      lokasi: null,
      sto: null,
      device: null,
      redaman: null,
      availability: 0,
      isSuspend: false,
    },
  });

  const { t } = useTranslation();

  return (
    <DataContext.Provider value={{ data, isLoading, error, t }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <DeviceDescriptionCard />
        <Card style={{ padding: "1rem" }}>
        <AvailabilityCard />
        <br />
        <DetailAnalysisCard />
        <br /> <hr /> <br />
        <Recommendations rootCause={
          data.status.toLowerCase() === "no_data" ? 
          data.isSuspend ? ["suspend"] : ["unknown"] :
          data.status.toLowerCase() === "dying gasp" ? ["dyingGasp"] : [data.status.toLowerCase()]} />
        </Card>
      </div>
    </DataContext.Provider>
  );
};

// prettier-ignore
const statusColors = {
  "ONLINE": Colors.alertsgreen100,
  "OFFLINE": Colors.alertsred100,
  "SUSPEND": Colors.alertsyellow100,
  "TECHNICAL ISSUE": "#a7a9ac",
  "TECH ISSUE": "#a7a9ac"
}

const DeviceDescriptionCard = () => {
  const { data, isLoading, error } = useContext(DataContext);

  return (
    <Card
      style={{
        width: "80%",
        maxWidth: "400px",
        backgroundColor: Colors.primary100,
        color: Colors.white,
        padding: "0.5rem 1rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "bold",
        }}
      >
        <div>{isLoading || error ? "Device Name" : data.name}</div>
        {!isLoading && !error && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Circle color={statusColors[data.status]} diameter="1rem" />
            {data.status}
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>{isLoading || error ? "xxx.xxx.xxx.xxx" : data.id}</div>
        <div>{!isLoading && !error && data.tgl_ukur}</div>
      </div>
    </Card>
  );
};

const AvailabilityCard = () => {
  const { data, isLoading, error, t } = useContext(DataContext);
  const availabilityChartRef = useRef(null);

  const levelColor = {
    normal: Colors.alertsgreen100,
    poor: Colors.alertsred100,
    critical: Colors.alertsred100,
  };

  useEffect(() => {
    if (data) {
      const availabilityChart = echarts.init(
        availabilityChartRef.current,
        null,
        {
          width: 70,
          height: 70,
        }
      );
      const availabilityChartOption = generateCircularProgressBarOption(
        `${data.availability}%`,
        null,
        levelColor.normal,
        data.availability
      );
      availabilityChart.setOption(availabilityChartOption);
    }
  }, [data]);

  return (
    <div style={{  }}>
      <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
        {t("hi.deviceDetail.availability")}
      </div>
      <div
        style={{
          border: "1px solid #bfbfbf",
          borderRadius: "5px",
          height: "70%",
          display: "flex",
          justifyContent: "start",
        }}
      >
        <div style={{ marginLeft: "2rem" }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div ref={availabilityChartRef}></div>
          </div>
          <div style={{ textAlign: "center", fontSize: "0.8rem" }}>Normal</div>
        </div>
      </div>
    </div>
  );
};

const DetailAnalysisCard = () => {
  const { data, isLoading, error, t} = useContext(DataContext);
  return (
    <div style={{  }}>
      <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
        {t("hi.deviceDetail.detailAnalysis")}
      </div>
      <div
        style={{
          padding: "0.5rem 1rem",
          borderRadius: "5px",
          height: "70%",
          display: "flex",
          justifyContent: "start",
          backgroundColor: Colors.neutral2,
        }}
      >
        <div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.deviceName")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && data.name}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.status")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && data.status.toUpperCase()}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.location")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && data.lokasi}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.sto")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && data.sto}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.deviceMerk")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && data.device}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.attenuation")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && data.redaman}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.measurementDate")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && data.tgl_ukur}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
